import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { formatDateOnlyMDY } from "../../utils/date";

export const members = [];

export const defaultMembersList = [];
export const manuallyAddedMembersList = [];

export const ubtSummaryList = [];

// Async thunk to fetch membership summary
export const fetchMembershipSummary = createAsyncThunk(
  "members/fetchMembershipSummary",
  async (
    {
      teamId,
      defaultMembership,
    }: {
      teamId: number | string;
      defaultMembership?: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
        },
      };

      const url = endpoints.ubt.members.getMembershipSummary(
        teamId,
        defaultMembership,
      );
      const response = await api.get(url, options);

      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch membership summary",
      );
    }
  },
);

/**
 * Fetch member page access permissions for a team.
 * @param teamId - Team identifier used in member access endpoint.
 * @param nuid - Optional NUID used for x-nuid header.
 */
export const fetchMemberAccess = createAsyncThunk(
  "members/fetchMemberAccess",
  async (
    {
      teamId,
      nuid,
    }: {
      teamId: number | string;
      nuid?: string | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const response = await api.get(
        endpoints.ubt.members.getMemberAccess(teamId),
        options,
      );
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch member access",
      );
    }
  },
);

// Helper function to handle API errors
const handleApiError = (
  error: any,
  rejectWithValue: any,
  defaultMessage: string,
) => {
  if (
    error?.message === "canceled" ||
    error?.message === "Request cancelled" ||
    error?.code === "ECONNABORTED"
  ) {
    return rejectWithValue({ message: "Request cancelled", status: null });
  }

  const errorMessage =
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.response?.data?.detail ??
    error?.response?.statusText ??
    error?.message ??
    defaultMessage;

  // Try multiple ways to get the status code
  const statusCode =
    error?.response?.status ??
    error?.status ??
    error?.statusCode ??
    error?.response?.statusCode ??
    null;

  return rejectWithValue({ message: errorMessage, status: statusCode });
};

// Helper function to convert Y/N to Yes/No
const formatYesNo = (value: string): string => {
  if (!value) return "";
  const upperValue = String(value).toUpperCase();
  if (upperValue === "Y") return "Yes";
  if (upperValue === "N") return "No";
  return value; // Return original value if not Y or N
};

// Async thunk to fetch team members
export const fetchTeamMembers = createAsyncThunk(
  "members/fetchTeamMembers",
  async (
    {
      teamId,
    }: {
      teamId: number | string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
        },
      };

      const response = await api.get(
        endpoints.ubt.members.getTeamMembers(teamId),
        options,
      );
      // Return both teamDetails and contact data
      return {
        teamDetails: response.data.teamDetails,
        contacts: response.data.contact,
      };
    } catch (error: any) {
      // Special handling: API returns 500 but body has nested 404s for teamDetails/contact
      const errors = error?.response?.data?.errors;
      if (
        error?.response?.data?.message === "Both API calls failed" &&
        errors?.teamDetails?.status === 404 &&
        errors?.contact?.status === 404
      ) {
        return rejectWithValue({
          message: error.response.data.message,
          status: 404,
        });
      }
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch team members",
      );
    }
  },
);

// Async thunk to fetch default members list
export const fetchDefaultMembers = createAsyncThunk(
  "members/fetchDefaultMembers",
  async (
    {
      teamId,
      sortColumn = "LAST_NAME",
      sortOrder = "ASC",
      page = 1,
      per_page = 10,
    }: {
      teamId: number | string;
      sortColumn?: string;
      sortOrder?: string;
      page?: number;
      per_page?: number;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
        },
      };

      const url = endpoints.ubt.members.getDefaultMembers(teamId, {
        sortColumn,
        sortOrder,
        page,
        per_page,
      });

      const response = await api.get(url, options);

      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch default members",
      );
    }
  },
);

// Async thunk to fetch manually added members list
export const fetchManualMembers = createAsyncThunk(
  "members/fetchManualMembers",
  async (
    {
      teamId,
      sortColumn = "LAST_NAME",
      sortOrder = "ASC",
      page = 1,
      per_page = 10,
    }: {
      teamId: number | string;
      sortColumn?: string;
      sortOrder?: string;
      page?: number;
      per_page?: number;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
        },
      };

      const url = endpoints.ubt.members.getManualMembers(teamId, {
        sortColumn,
        sortOrder,
        page,
        per_page,
      });

      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch manual members",
      );
    }
  },
);

// Async thunk to fetch team-member role status (options for the Team Role dropdown)
export const fetchMemberRoleOptions = createAsyncThunk(
  "members/fetchMemberRoleOptions",
  async (
    { member }: { member: "default" | "manual" },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        headers: {
          accept: "application/json",
        },
      };

      const url = endpoints.ubt.members.getTeamMemberRoleStatus(member);
      const response = await api.get(url, options);

      return { member, data: response.data };
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to fetch member role options",
      );
    }
  },
);

// Async thunk to update a team member's role via PUT /ubt/team-member/status
export const updateTeamMemberRole = createAsyncThunk(
  "members/updateTeamMemberRole",
  async (
    {
      memberType,
      nuid,
      teamId,
      newStatus,
      currentStatus,
      startDate,
      deptPk,
      privStartDate,
    }: {
      memberType: "default" | "manual";
      nuid: string;
      teamId: number | string;
      newStatus: string;
      currentStatus: string;
      startDate?: string;
      deptPk?: number | null;
      privStartDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url =
        memberType === "manual"
          ? endpoints.ubt.members.updateManualTeamMemberStatus
          : endpoints.ubt.members.updateTeamMemberStatus;
      const response = await api.put(
        url,
        {
          nuid,
          teamId,
          newStatus,
          currentStatus,
          startDate,
          deptPk,
          privStartDate,
        },
        {
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
        },
      );
      return { memberType, nuid, newStatus, data: response.data };
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to update member role",
      );
    }
  },
);

// Async thunk to add a member to a team via POST /ubt/team-member
export const addTeamMember = createAsyncThunk(
  "members/addTeamMember",
  async (
    {
      teamId,
      deptPk,
      nuid,
    }: { teamId: number | string; deptPk?: number | null; nuid: string },
    { rejectWithValue },
  ) => {
    try {
      const body: Record<string, any> = { teamId, nuid };
      if (deptPk != null) body.deptPk = deptPk;
      const response = await api.post(
        endpoints.ubt.members.addTeamMember,
        body,
        {
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to add team member",
      );
    }
  },
);

// Async thunk to search team members by nuid or name
export const searchTeamMembers = createAsyncThunk(
  "members/searchTeamMembers",
  async (
    params: {
      searchBy: "nuid" | "name";
      teamId: number | string;
      defaultMembership?: string;
      nuid?: string;
      firstName?: string;
      lastName?: string;
      sortColumn?: string;
      sortOrder?: string;
      page?: number;
      per_page?: number;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const url = endpoints.ubt.members.searchTeamMembers(params);
      const response = await api.get(url, {
        signal,
        headers: { accept: "application/json" },
      });
      return response.data;
    } catch (error: any) {
      return handleApiError(
        error,
        rejectWithValue,
        "Failed to search team members",
      );
    }
  },
);

// Helper function to transform contacts into sponsor list format
const transformContactsToSponsors = (contacts: any[]) => {
  if (!contacts || !Array.isArray(contacts)) return {};

  const sponsors: any = {
    "Sponsors & Labor Sponsors": { name: [], emailList: [] },
    "Co-Leads & Labor Co-Leads": { name: [], emailList: [] },
    Consultants: { name: [], emailList: [] },
    "TH/WPS Champion": { name: [], emailList: [] },
  };

  contacts.forEach((contact: any) => {
    const { contactName, contactEmail, teamRole } = contact;

    if (!contactName) return;

    if (teamRole === "Labor Sponsor" || teamRole === "Sponsor") {
      sponsors["Sponsors & Labor Sponsors"].name.push(contactName);
      if (contactEmail)
        sponsors["Sponsors & Labor Sponsors"].emailList.push(contactEmail);
    } else if (teamRole === "Labor Co-Lead" || teamRole === "Co-Lead") {
      sponsors["Co-Leads & Labor Co-Leads"].name.push(contactName);
      if (contactEmail)
        sponsors["Co-Leads & Labor Co-Leads"].emailList.push(contactEmail);
    } else if (teamRole === "Consultant") {
      sponsors["Consultants"].name.push(contactName);
      if (contactEmail) sponsors["Consultants"].emailList.push(contactEmail);
    } else if (teamRole === "TH/WPS Champion" || teamRole === "Champion") {
      sponsors["TH/WPS Champion"].name.push(contactName);
      if (contactEmail)
        sponsors["TH/WPS Champion"].emailList.push(contactEmail);
    }
  });

  // Remove empty sponsor categories
  Object.keys(sponsors).forEach((key) => {
    if (sponsors[key].name.length === 0) {
      delete sponsors[key];
    }
  });

  return sponsors;
};

const membersSlice = createSlice({
  name: "membersSlice",
  initialState: {
    membersList: members,
    teamDetails: null as any,
    contacts: [] as any[],
    sponsorsList: {} as any,
    defaultMembers: [] as any[],
    defaultMembersTotal: 0,
    defaultMembersPage: 1,
    defaultMembersPerPage: 10,
    defaultMembersSortColumn: "LAST_NAME",
    defaultMembersSortOrder: "ASC" as "ASC" | "DESC",
    manualMembers: [] as any[],
    manualMembersTotal: 0,
    manualMembersPage: 1,
    manualMembersPerPage: 10,
    manualMembersSortColumn: "LAST_NAME",
    manualMembersSortOrder: "ASC" as "ASC" | "DESC",
    manualMembersLoading: false,
    manualMembersFetched: false,
    membershipSummary: [] as any[],
    membershipSummaryLoading: false,
    membershipSummaryError: null as string | null,
    memberAccess: null as
      | { canEdit?: boolean; canMemberShipEdit?: boolean; isAdmin?: boolean }
      | null,
    memberAccessLoading: false,
    memberAccessError: null as string | null,
    currentPage: 1,
    perPage: 10,
    totalMembers: 0,
    loading: false,
    defaultMembersLoading: false,
    defaultMembersFetched: false,
    defaultMembersError: null as string | null,
    // Role options for team role dropdown per member type (default/manual)
    memberRoleOptionsDefault: [] as any[],
    memberRoleOptionsManual: [] as any[],
    memberRoleOptionsLoading: false,
    memberRoleOptionsError: null as string | null,
    updateMemberRoleLoading: false,
    updateMemberRoleError: null as string | null,
    // Team member search results
    searchResults: [] as any[],
    searchTotal: 0,
    searchLoading: false,
    searchError: null as string | null,
    // Search filters state (persisted across tab switches)
    searchBy: "name" as "name" | "nuid",
    searchNuid: "",
    searchFirstName: "",
    searchLastName: "",
    searchHasSearched: false,
    searchPage: 1,
    searchSortColumn: "lastName",
    searchSortDirection: "asc" as "asc" | "desc",
    // Add team member
    addMemberLoading: false,
    addMemberError: null as string | null,
    error: null as string | null,
    teamMembersError: null as string | null,
    teamMembersStatus: null as number | null,
    manualMembersError: null as string | null,
  },
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setPerPage(state, action) {
      state.perPage = action.payload;
    },
    setDefaultMembersPage(state, action) {
      state.defaultMembersPage = action.payload;
    },
    setDefaultMembersSorting(state, action) {
      state.defaultMembersSortColumn = action.payload.sortColumn;
      state.defaultMembersSortOrder = action.payload.sortOrder;
    },
    setManualMembersPage(state, action) {
      state.manualMembersPage = action.payload;
    },
    setManualMembersSorting(state, action) {
      state.manualMembersSortColumn = action.payload.sortColumn;
      state.manualMembersSortOrder = action.payload.sortOrder;
    },
    // Clear manual members when team changes so next tab click fetches fresh data
    clearManualMembers(state) {
      state.manualMembers = [];
      state.manualMembersTotal = 0;
      state.manualMembersPage = 1;
      state.manualMembersLoading = false;
      state.manualMembersFetched = false;
    },
    setSearchBy(state, action) {
      state.searchBy = action.payload;
    },
    setSearchNuid(state, action) {
      state.searchNuid = action.payload;
    },
    setSearchFirstName(state, action) {
      state.searchFirstName = action.payload;
    },
    setSearchLastName(state, action) {
      state.searchLastName = action.payload;
    },
    setSearchHasSearched(state, action) {
      state.searchHasSearched = action.payload;
    },
    setSearchPage(state, action) {
      state.searchPage = action.payload;
    },
    setSearchSorting(state, action) {
      state.searchSortColumn = action.payload.sortColumn;
      state.searchSortDirection = action.payload.sortDirection;
    },
    resetSearch(state) {
      state.searchBy = "name";
      state.searchNuid = "";
      state.searchFirstName = "";
      state.searchLastName = "";
      state.searchHasSearched = false;
      state.searchPage = 1;
      state.searchSortColumn = "lastName";
      state.searchSortDirection = "asc";
      state.searchResults = [];
      state.searchTotal = 0;
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTeamMembers.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.teamMembersError = null;
      state.teamMembersStatus = null;
    });
    builder.addCase(fetchTeamMembers.fulfilled, (state, action) => {
      state.loading = false;
      state.teamMembersError = null;
      state.teamMembersStatus = null;
      const payload: any = action.payload;

      // Map API response to members format
      if (Array.isArray(payload?.teamDetails) && payload.teamDetails.length > 0) {
        // The API returns team details ordered with the active team last.
        const latestTeam = payload.teamDetails[payload.teamDetails.length - 1];

        state.teamDetails = {
          teamId: latestTeam.teamId,
          teamName: latestTeam.teamName,
          teamDateCreated: formatDateOnlyMDY(latestTeam.teamDateCreated),
          teamRating: latestTeam.teamRating,
          kpGlRegionCd: latestTeam.kpGlRegionCd,
          serviceDeliveryType: latestTeam.serviceDeliveryType,
          usingHuddles: formatYesNo(latestTeam.usingHuddles),
          defaultMembership: formatYesNo(latestTeam.defaultMembership),
        };

        // Transform the API response to match the members structure for GL strings table
        const transformedMembers =
          payload.teamDetails?.map((item: any) => ({
            glString: item.glString,
            ubtName: item.teamName,
            entity: item.entity,
            location: item.location,
            costCenter: item.costCenter,
            shift: item.shift ?? "All",
            employees: item.employeeCount ?? 0,
          })) ?? [];

        state.membersList = transformedMembers;
        state.totalMembers = transformedMembers.length ?? 0;
      } else {
        state.teamDetails = null;
        state.membersList = [];
        state.totalMembers = 0;
      }

      // Store contacts and transform to sponsors list
      if (payload?.contacts) {
        state.contacts = payload.contacts;
        state.sponsorsList = transformContactsToSponsors(payload.contacts);
      }
    });
    builder.addCase(fetchTeamMembers.rejected, (state, action) => {
      state.loading = false;
      const payload = action.payload as any;
      state.error = payload?.message ?? payload;
      state.teamMembersError = payload?.message ?? payload;
      state.teamMembersStatus = payload?.status ?? null;
      state.teamDetails = null;
      state.membersList = [];
      state.totalMembers = 0;
    });

    // Handle fetchDefaultMembers
    builder.addCase(fetchDefaultMembers.pending, (state) => {
      state.defaultMembersLoading = true;
      state.error = null;
      state.defaultMembersError = null;
      state.defaultMembersFetched = false;
    });
    builder.addCase(fetchDefaultMembers.fulfilled, (state, action) => {
      state.defaultMembersLoading = false;
      state.defaultMembersFetched = true;
      const payload: any = action.payload;
      state.defaultMembersError = null;

      if (payload) {
        // Transform the API response to match the defaultMembers structure
        // API returns { items: [...], meta: { total, page, ... } }
        const transformedDefaultMembers =
          payload.items?.map((item: any) => ({
            lastName: item.lastName ?? "",
            firstName: item.firstName ?? "",
            nuid: item.nuid ?? "",
            unionCode: item.unionCode ?? "",
            ckpu: item.ckpu ?? "",
            teamRole: item.teamRole ?? "Member",
            startDate: item.startDate ?? item.startdate ?? "",
            deptPk: item.deptPk ?? null,
            privStartDate: item.privStartDate ?? item.privlegestartdate ?? "",
            startdate: item.startdate,
            privlegestartdate: item.privlegestartdate,
          })) ?? [];

        state.defaultMembers = transformedDefaultMembers;
        state.defaultMembersTotal =
          payload.meta?.total ?? transformedDefaultMembers.length;
      }
    });
    builder.addCase(fetchDefaultMembers.rejected, (state, action) => {
      state.defaultMembersLoading = false;
      const payload = action.payload as any;
      state.error = payload?.message ?? payload;
      state.defaultMembersError = payload?.message ?? payload;
      state.defaultMembersFetched = true;
    });

    // Handle fetchManualMembers
    builder.addCase(fetchManualMembers.pending, (state) => {
      state.manualMembersLoading = true;
      state.error = null;
      state.manualMembersError = null;
      state.manualMembersFetched = false;
    });
    builder.addCase(fetchManualMembers.fulfilled, (state, action) => {
      state.manualMembersLoading = false;
      state.manualMembersFetched = true;
      const payload: any = action.payload;
      state.manualMembersError = null;

      if (payload) {
        // Transform the API response to match the manualMembers structure
        // API returns { items: [...], meta: { total, page, ... } }
        const transformedManualMembers =
          payload.items?.map((item: any) => ({
            lastName: item.lastName ?? "",
            firstName: item.firstName ?? "",
            nuid: item.nuid ?? "",
            unionCode: item.unionCode ?? "",
            ckpu: item.ckpu ?? "",
            teamRole: item.teamRole ?? "Member",
            startDate: item.startDate ?? item.startdate ?? "",
            deptPk: item.deptPk ?? null,
            privStartDate: item.privStartDate ?? item.privlegestartdate ?? "",
            startdate: item.startdate,
            privlegestartdate: item.privlegestartdate,
          })) ?? [];

        state.manualMembers = transformedManualMembers;
        state.manualMembersTotal =
          payload.meta?.total ?? transformedManualMembers.length;
      }
    });
    builder.addCase(fetchManualMembers.rejected, (state, action) => {
      state.manualMembersLoading = false;
      const payload = action.payload as any;
      state.error = payload?.message ?? payload;
      state.manualMembersError = payload?.message ?? payload;
      state.manualMembersFetched = true;
    });

    // Handle fetchMemberRoleOptions
    builder.addCase(fetchMemberRoleOptions.pending, (state) => {
      state.memberRoleOptionsLoading = true;
      state.memberRoleOptionsError = null;
    });
    builder.addCase(fetchMemberRoleOptions.fulfilled, (state, action) => {
      state.memberRoleOptionsLoading = false;
      state.memberRoleOptionsError = null;
      const payload: any = action.payload;
      if (payload?.member === "default") {
        state.memberRoleOptionsDefault = payload.data ?? [];
      } else if (payload?.member === "manual") {
        state.memberRoleOptionsManual = payload.data ?? [];
      }
    });
    builder.addCase(fetchMemberRoleOptions.rejected, (state, action) => {
      state.memberRoleOptionsLoading = false;
      const payload = action.payload as any;
      state.memberRoleOptionsError = payload?.message ?? payload;
    });

    // Handle fetchMembershipSummary
    builder.addCase(fetchMembershipSummary.pending, (state) => {
      state.membershipSummaryLoading = true;
      state.error = null;
      state.membershipSummaryError = null;
    });
    builder.addCase(fetchMembershipSummary.fulfilled, (state, action) => {
      state.membershipSummaryLoading = false;
      const payload: any = action.payload;
      state.membershipSummaryError = null;

      if (payload) {
        // Transform the API response to match the table structure
        const transformedSummary = [];

        // Map membershipSummary array items
        if (Array.isArray(payload.membershipSummary)) {
          payload.membershipSummary.forEach((item: any) => {
            // Custom label mapping based on categoryKey
            let label = item.categoryLabel ?? "";
            if (item.categoryKey === "coLeads") {
              label = "Team co-leads and labor co-leads and proxy";
            } else if (item.categoryKey === "teamMembers") {
              label = "Team members (total-excluded)";
            } else if (item.categoryKey === "defaultedMembersExcl") {
              label = "Defaulted members excluded";
            } else if (item.categoryKey === "teamSponsors") {
              label = "Team sponsors and labor-sponsors";
            }

            transformedSummary.push({
              ubtMembershipSummary: label,
              defaulted: item.defaulted ?? "n/a",
              manuallyAdded: item.manuallyAdded ?? "n/a",
              total: item.total ?? "n/a",
            });
          });
        }

        // Add grandTotal as the last row
        if (payload.grandTotal) {
          transformedSummary.push({
            ubtMembershipSummary: "Total",
            defaulted: "",
            manuallyAdded: "",
            total: payload.grandTotal.total ?? "",
          });
        }

        state.membershipSummary = transformedSummary;
      }
    });
    builder.addCase(fetchMembershipSummary.rejected, (state, action) => {
      state.membershipSummaryLoading = false;
      const payload = action.payload as any;
      state.error = payload?.message ?? payload;
      state.membershipSummaryError = payload?.message ?? payload;
    });

    // Handle fetchMemberAccess
    builder.addCase(fetchMemberAccess.pending, (state) => {
      state.memberAccessLoading = true;
      state.memberAccessError = null;
    });
    builder.addCase(fetchMemberAccess.fulfilled, (state, action) => {
      state.memberAccessLoading = false;
      state.memberAccessError = null;
      state.memberAccess = action.payload ?? null;
    });
    builder.addCase(fetchMemberAccess.rejected, (state, action) => {
      state.memberAccessLoading = false;
      const payload = action.payload as any;
      state.memberAccessError = payload?.message ?? payload;
      state.memberAccess = null;
    });

    // Handle updateTeamMemberRole
    builder.addCase(updateTeamMemberRole.pending, (state) => {
      state.updateMemberRoleLoading = true;
      state.updateMemberRoleError = null;
    });
    builder.addCase(updateTeamMemberRole.fulfilled, (state, action) => {
      state.updateMemberRoleLoading = false;
      state.updateMemberRoleError = null;
      const { memberType, nuid, newStatus } = action.payload;
      // Optimistically update the teamRole in the appropriate list
      if (memberType === "default") {
        state.defaultMembers = state.defaultMembers.map((m: any) =>
          m.nuid === nuid ? { ...m, teamRole: newStatus } : m,
        );
      } else if (memberType === "manual") {
        state.manualMembers = state.manualMembers.map((m: any) =>
          m.nuid === nuid ? { ...m, teamRole: newStatus } : m,
        );
      }
    });
    builder.addCase(updateTeamMemberRole.rejected, (state, action) => {
      state.updateMemberRoleLoading = false;
      const payload = action.payload as any;
      state.updateMemberRoleError = payload?.message ?? payload;
    });

    // Handle searchTeamMembers
    builder.addCase(searchTeamMembers.pending, (state) => {
      state.searchLoading = true;
      state.searchError = null;
    });
    builder.addCase(searchTeamMembers.fulfilled, (state, action) => {
      state.searchLoading = false;
      state.searchError = null;
      const payload: any = action.payload;
      state.searchResults =
        payload?.items?.map((item: any) => ({
          lastName: item.lastName ?? "",
          firstName: item.firstName ?? "",
          nuid: item.nuid ?? "",
          location: item.location ?? "",
          unionCode: item.unionCd ?? item.unionCode ?? "",
          ckpu: item.ckpu ?? "",
          teamRole: item.teamRole ?? "Member",
          startDate: item.startDate ?? "",
          deptpk: item.deptpk ?? item.deptPk ?? null,
          privStartDate: item.privStartDate ?? "",
        })) ?? [];
      state.searchTotal = payload?.meta?.total ?? state.searchResults.length;
    });
    builder.addCase(searchTeamMembers.rejected, (state, action) => {
      state.searchLoading = false;
      const payload = action.payload as any;
      state.searchError = payload?.message ?? payload;
    });

    // Handle addTeamMember
    builder.addCase(addTeamMember.pending, (state) => {
      state.addMemberLoading = true;
      state.addMemberError = null;
    });
    builder.addCase(addTeamMember.fulfilled, (state) => {
      state.addMemberLoading = false;
      state.addMemberError = null;
    });
    builder.addCase(addTeamMember.rejected, (state, action) => {
      state.addMemberLoading = false;
      const payload = action.payload as any;
      state.addMemberError = payload?.details?.[0]?.message ?? payload;
    });
  },
});

export const {
  setCurrentPage,
  setPerPage,
  setDefaultMembersPage,
  setDefaultMembersSorting,
  setManualMembersPage,
  setManualMembersSorting,
  clearManualMembers,
  setSearchBy,
  setSearchNuid,
  setSearchFirstName,
  setSearchLastName,
  setSearchHasSearched,
  setSearchPage,
  setSearchSorting,
  resetSearch,
} = membersSlice.actions;

export default membersSlice.reducer;
